// Owner home screen — thin adapter around the owner branch that previously
// lived inline in app/dashboard/home.tsx. The JSX is intentionally identical
// to the legacy inline render so the new feature flag is a pure render-path
// switch (flag OFF = old inline branch, flag ON = this component). Rollback
// is simply flipping EXPO_PUBLIC_OWNER_HOME_SCREEN_ENABLED back to off and
// rebuilding.

import { View, StyleSheet } from 'react-native';
import { Surface } from 'react-native-paper';
import { useLicense } from '../../../context/LicenseContext';
import { useEffect } from 'react';
import {
  deriveOwnerBecomeDriverSeatState,
  resolveAvailableCompanySeats,
} from '../ownerBecomeDriver';
import { useOwnerSeatRefresh } from '../useOwnerSeatRefresh';
import { isOwnerDriverProfileComplete } from '../ownerDriverPrompt';
import { Sidebar, SIDEBAR_WIDTH } from '../../../components/Sidebar';
import { OwnerDashboardContent } from '../../../components/OwnerDashboardContent';
import { UnifiedBuyLicensesModal } from '../../../components/UnifiedBuyLicensesModal';
import { InviteDriverModal } from '../../../components/InviteDriverModal';
import { CompleteDriverProfileModal } from '../../../components/CompleteDriverProfileModal';
import { getOnboardingRedirect } from '../../../utils/onboardingRedirect';
import type { DriverProfileBoundaryResponse } from '../../../api/apiService';

type ProfileValue = 'Owner' | 'Driver';

export interface OwnerHomeScreenProps {
  // user / company
  userData: any;
  companyStats: any;
  // layout
  width: number;
  isLargeScreen: boolean;
  desktopSidebarVisible: boolean;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  handleLogout: () => Promise<void>;
  hasDriver: boolean;
  effectiveProfile: ProfileValue;
  updateProfile: (profile: ProfileValue) => void;
  // routing
  router: any;
  theme: any;
  // modals
  buyLicensesModalVisible: boolean;
  setBuyLicensesModalVisible: (visible: boolean) => void;
  inviteModalVisible: boolean;
  setInviteModalVisible: (visible: boolean) => void;
  completeDriverProfileModalVisible: boolean;
  setCompleteDriverProfileModalVisible: (visible: boolean) => void;
  onCompleteDriverProfileSuccess: (result?: DriverProfileBoundaryResponse) => void | Promise<void>;
  /** D-046: explicit become-driver action (form or saved-profile reuse). */
  onBecomeDriver: () => void | Promise<void>;

  // callbacks
  handleLicensePurchaseSuccessBase: (refreshLicenses: () => Promise<void>) => Promise<void>;
  setAlertMessage: (message: string) => void;
  setAlertModalVisible: (visible: boolean) => void;
  t: any;
  safeRedirect: (route: string) => void;
}

export function OwnerHomeScreen({
  userData,
  companyStats,
  width,
  isLargeScreen,
  desktopSidebarVisible,
  sidebarOpen,
  setSidebarOpen,
  handleLogout,
  hasDriver,
  effectiveProfile,
  updateProfile,
  router,
  theme,
  buyLicensesModalVisible,
  setBuyLicensesModalVisible,
  inviteModalVisible,
  setInviteModalVisible,
  completeDriverProfileModalVisible,
  setCompleteDriverProfileModalVisible,
  onCompleteDriverProfileSuccess,
  onBecomeDriver,

  handleLicensePurchaseSuccessBase,
  setAlertMessage,
  setAlertModalVisible,
  t,
  safeRedirect,
}: OwnerHomeScreenProps) {
  const { licenseInfo, isLoading: isLicenseLoading, error: licenseError, refreshLicenses } = useLicense();

  // D-046 (2026-10-11): capacity-aware owner become-driver presentation derived
  // from the already-loaded company licence state. Unknown/loading/error is
  // never presented as sold out.
  const becomeDriverSeatState = deriveOwnerBecomeDriverSeatState({
    hasLicenseInfo: licenseInfo !== null,
    isLoading: isLicenseLoading,
    hasError: Boolean(licenseError),
    availableSeats: resolveAvailableCompanySeats(licenseInfo),
  });

  useOwnerSeatRefresh(!hasDriver && becomeDriverSeatState === 'full', refreshLicenses);

  const handleBecomeDriverAction = async () => {
    await onBecomeDriver();
    // Do not refresh while opening a new form: loading hides/remounts it.
    if (isOwnerDriverProfileComplete(userData?.profile)) await refreshLicenses();
  };

  const handleCompleteDriverProfileSuccessWithRefresh = async (result?: DriverProfileBoundaryResponse) => {
    await onCompleteDriverProfileSuccess(result);
    await refreshLicenses();
  };

  // The extracted owner surface follows the same server-authoritative route as
  // login and dashboard entry. License history and device storage are display
  // signals only; they cannot send an incomplete owner back to this screen.
  useEffect(() => {
    if (!userData) return;
    let cancelled = false;
    void getOnboardingRedirect({ userData }).then((result) => {
      if (!cancelled && result.route && result.route !== '/dashboard/home') {
        safeRedirect(result.route);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [safeRedirect, userData]);

  const handleLicensePurchaseSuccess = () => {
    return handleLicensePurchaseSuccessBase(refreshLicenses);
  };
  return (
    <View style={styles.container}>
      {desktopSidebarVisible && (
        <Sidebar
          isOpen={true}
          onClose={() => {}}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={true}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
          onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
          onCompleteDriverProfile={!hasDriver && becomeDriverSeatState === 'available' ? () => { void handleBecomeDriverAction(); } : undefined}
          showBecomeDriver={!hasDriver && becomeDriverSeatState === 'available'}
        />
      )}

      <Surface style={{ flex: 1, backgroundColor: theme.colors.background, ...(desktopSidebarVisible ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
        <OwnerDashboardContent
          userData={userData || { company: null }}
          licenseInfo={licenseInfo}
          companyStats={companyStats}
          isLoading={isLicenseLoading}
          onOpenSidebar={() => setSidebarOpen(true)}
          onBuyLicense={() => setBuyLicensesModalVisible(true)}
          onBuyGpsLicense={() => setBuyLicensesModalVisible(true)}
          onInviteDriver={() => setInviteModalVisible(true)}
          becomeDriverSeatState={!hasDriver ? becomeDriverSeatState : 'unknown'}
          onBecomeDriver={!hasDriver ? () => { void handleBecomeDriverAction(); } : undefined}
        />

        {!isLargeScreen && (
          <Sidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            screenWidth={width}
            onLogout={handleLogout}
            isOwner={true}
            isDriver={hasDriver}
            selectedProfile={effectiveProfile}
            onSwitchProfile={hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
            onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
            onCompleteDriverProfile={!hasDriver && becomeDriverSeatState === 'available' ? () => { void handleBecomeDriverAction(); } : undefined}
            showBecomeDriver={!hasDriver && becomeDriverSeatState === 'available'}
            />
        )}

        <UnifiedBuyLicensesModal
          visible={buyLicensesModalVisible}
          onDismiss={() => setBuyLicensesModalVisible(false)}
          onSuccess={handleLicensePurchaseSuccess}
        />
        <InviteDriverModal
          visible={inviteModalVisible}
          onDismiss={() => setInviteModalVisible(false)}
          onSuccess={() => {
            setAlertMessage(t('ownerDashboard.inviteSuccess', 'Invitation sent successfully'));
            setAlertModalVisible(true);
          }}
        />
        <CompleteDriverProfileModal
          visible={completeDriverProfileModalVisible}
          onDismiss={() => setCompleteDriverProfileModalVisible(false)}
          onSuccess={handleCompleteDriverProfileSuccessWithRefresh}
        />
      </Surface>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, flexDirection: 'row' },
});
